import { ShellSnippet } from '@/shared/components/ShellSnippet'
import { s } from '@/features/integrations/guideStyles'
import type { GuideId } from '@/features/integrations/guides'
import {     CLI_COMMAND_TREE } from '@/features/integrations/snippets'

export function CliCommandsGuide({ onSwitchGuide }: { onSwitchGuide: (id: GuideId) => void }) {
  return (
    <div>
      <h3 style={s.guideTitle}>Commands</h3>
      <p style={s.para}>
        The grammar is resource-first - <code style={s.code}>terramantle {'<resource> <verb>'}</code> - so the
        shape of a command is predictable even when you have not used it before.
      </p>
      <ShellSnippet code={CLI_COMMAND_TREE} language="text" />

      <h3 style={s.subTitle}>Global flags</h3>
      <p style={s.para}>Accepted by every subcommand:</p>
      <ul style={s.list}>
        <li><code style={s.code}>--org</code> / <code style={s.code}>--workspace</code> - override the context</li>
        <li><code style={s.code}>--context {'<name>'}</code> - use a different saved context for this call</li>
        <li><code style={s.code}>--api-url {'<url>'}</code> - point at another deployment</li>
        <li><code style={s.code}>-o, --output table|wide|json|yaml</code> - <code style={s.code}>json</code> for scripts</li>
        <li><code style={s.code}>--auth-mode auto|token|client|github|gitlab|device</code></li>
        <li><code style={s.code}>--no-color</code>, and <code style={s.code}>-v</code> (repeatable) for verbosity</li>
      </ul>

      <h3 style={s.subTitle}>Discovery</h3>
      <ShellSnippet code={`terramantle providers ls                  # usage rollup + Trust verdict per provider
terramantle providers ls --at-risk        # only the ones failing posture
terramantle providers show hashicorp/aws  # versions, trust, used-by workspaces

terramantle modules search database
terramantle modules show acme/vpc/aws`} language="bash" />

      <h3 style={s.subTitle}>Scaffolding a repo</h3>
      <p style={s.para}>
        <code style={s.code}>init</code> creates; <code style={s.code}>upgrade</code> re-renders - the same
        create / plan / apply loop as Terraform. The VCS is inferred from your{' '}
        <code style={s.code}>origin</code> remote, so the emitted pipeline is GitHub Actions or GitLab CI
        without being told.
      </p>
      <ShellSnippet code={`cd my-infra-repo && git init

terramantle init --artefact modules        # or: --artefact workspaces
terramantle init --structure mono          # many artefacts, <module>@<version> tags
terramantle init --ci-auth oidc --tf 1.7,1.9 --yes

terramantle upgrade --diff                 # show the plan (+ ~ -), change nothing
terramantle upgrade                        # apply it`} language="bash" />
      <p style={{ ...s.para, marginTop: 10 }}>
        <code style={s.code}>terramantle.hcl</code> is the repo manifest and your source of truth. Managed
        files carry a provenance header and are re-rendered on <code style={s.code}>upgrade</code>; skeleton
        files (<code style={s.code}>*.tf</code>, <code style={s.code}>README.md</code>) carry none and are
        yours after creation - never rewritten or pruned. Review with{' '}
        <code style={s.code}>--diff</code> first.
      </p>

      <h3 style={s.subTitle}>Publishing</h3>
      <p style={s.para}>
        Versions come from Conventional Commits since each artefact's last tag -{' '}
        <code style={s.code}>feat!</code> / <code style={s.code}>BREAKING CHANGE:</code> major,{' '}
        <code style={s.code}>feat:</code> minor, <code style={s.code}>fix:</code> patch. A module's first
        release is always <code style={s.code}>1.0.0</code>. Full walkthrough in{' '}
        <button style={s.linkBtn} onClick={() => onSwitchGuide('module-publishing')}>Publishing Modules</button>.
      </p>
      <ShellSnippet code={`terramantle modules changed --all -o json   # network-free, CI-consumable
terramantle modules publish --provider aws  # the changed set, computed versions
terramantle modules publish --module vpc --version 1.4.0 --provider aws
terramantle modules publish --all --bump minor --provider aws --dry-run

terramantle repo tag --dry-run              # the <module>@<version> tags it would push
terramantle repo tag`} language="bash" />

      <h3 style={s.subTitle}>Lock files and state</h3>
      <p style={s.para}>
        See <button style={s.linkBtn} onClick={() => onSwitchGuide('lock-files')}>Provider Lock Files</button>{' '}
        and <button style={s.linkBtn} onClick={() => onSwitchGuide('storing-state')}>Storing State</button> for
        the surrounding workflow.
      </p>
      <ShellSnippet code={`terramantle lock push [path] --fail-on-atrisk

terramantle state ls
terramantle state versions prod
terramantle state promote prod <versionId>
terramantle state rollback prod --to 41
terramantle state unlock prod               # force-unlock a stuck lock
terramantle state publish --all --fail-on-atrisk`} language="bash" />
      <p style={{ ...s.para, marginTop: 10 }}>
        The destructive state verbs confirm first; <code style={s.code}>--yes</code> skips the prompt and{' '}
        <code style={s.code}>--force</code> is permission-aware. Without either, a non-interactive run exits{' '}
        <code style={s.code}>4</code> rather than guessing.
      </p>

      <h3 style={s.subTitle}>Environment variables</h3>
      <ul style={s.list}>
        <li><code style={s.code}>TERRAMANTLE_API_URL</code> - API base; defaults to <code style={s.code}>https://registry.terramantle.dev</code></li>
        <li><code style={s.code}>TERRAMANTLE_ORG</code> / <code style={s.code}>TERRAMANTLE_WORKSPACE</code> / <code style={s.code}>TERRAMANTLE_CONTEXT</code></li>
        <li><code style={s.code}>TERRAMANTLE_TOKEN</code> - raw bearer, skipping every auth flow</li>
        <li><code style={s.code}>TERRAMANTLE_CLIENT_ID</code> / <code style={s.code}>TERRAMANTLE_CLIENT_SECRET</code> - bot client credentials</li>
        <li><code style={s.code}>TERRAMANTLE_ID_TOKEN</code> - the GitLab CI OIDC id-token</li>
        <li><code style={s.code}>TERRAMANTLE_OIDC_ISSUER</code> / <code style={s.code}>TERRAMANTLE_AUDIENCE</code> / <code style={s.code}>TERRAMANTLE_PORTAL_URL</code> - override what discovery returns</li>
        <li><code style={s.code}>TERRAMANTLE_OUTPUT</code> (default <code style={s.code}>table</code>),{' '}
          <code style={s.code}>TERRAMANTLE_AUTH_MODE</code> (default <code style={s.code}>auto</code>),{' '}
          <code style={s.code}>NO_COLOR</code></li>
      </ul>

      <h3 style={s.subTitle}>Exit codes</h3>
      <p style={s.para}>Stable enough to branch on in a pipeline:</p>
      <ul style={s.list}>
        <li><code style={s.code}>0</code> success · <code style={s.code}>1</code> generic error ·{' '}
          <code style={s.code}>2</code> usage error (bad flags or args)</li>
        <li><code style={s.code}>3</code> posture gate tripped - what <code style={s.code}>--fail-on-atrisk</code> returns</li>
        <li><code style={s.code}>4</code> confirmation required but refused, or non-interactive without <code style={s.code}>--yes</code></li>
        <li><code style={s.code}>5</code> auth error (missing, invalid or expired token, or a missing role)</li>
        <li><code style={s.code}>6</code> not found (org, workspace or version) · <code style={s.code}>7</code> state
          conflict - the workspace is locked or the serial conflicts</li>
      </ul>
    </div>
  )
}
